/**
 * <HostNoteModal> - Iter 9.40 Pass C
 *
 * Surfaces host-curated notes (Pre / During / Post stage) at the right time in
 * the client RSVP / experience flow. Backend route: GET /api/host/notes.
 *
 * Trigger logic:
 *   - `pre`     → shown after RSVP is submitted ("here's what to know before").
 *   - `during`  → shown when host opens the event page ("during your appointment").
 *   - `post`    → shown after the experience is marked complete.
 *
 * Notes can be field-triggered (e.g. only show "foundation_shade" notes when
 * the client picked one) via `trigger_field_keys`. `always_show` overrides.
 */
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sparkles, Clock, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGE_META = {
  pre:    { label: "Before your appointment", icon: Clock,    accent: "bg-amber-50 text-amber-700 border-amber-200" },
  during: { label: "During your appointment", icon: Sparkles, accent: "bg-purple-50 text-purple-700 border-purple-200" },
  post:   { label: "After your appointment",  icon: Check,    accent: "bg-green-50 text-green-700 border-green-200" },
};

const matchesTriggers = (note, clientPreferences = {}) => {
  if (note.always_show) return true;
  const fieldKeys = note.trigger_field_keys || [];
  if (fieldKeys.length === 0) return true;
  return fieldKeys.some((key) => {
    const v = clientPreferences[key];
    return Array.isArray(v) ? v.length > 0 : Boolean(v);
  });
};

export const HostNoteModal = ({
  open,
  onClose,
  hostUserId,
  stage,                  // "pre" | "during" | "post"
  categoryId,
  clientPreferences = {}, // for trigger_field_keys matching
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !hostUserId) return;
    setLoading(true);
    (async () => {
      try {
        const params = new URLSearchParams({ host_user_id: hostUserId });
        if (stage) params.append("stage", stage);
        if (categoryId) params.append("category_id", categoryId);
        const token = localStorage.getItem("session_token");
        const res = await fetch(`${API}/host/notes?${params}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setNotes(data.notes || []);
        }
      } catch { /* silent */ }
      setLoading(false);
    })();
  }, [open, hostUserId, stage, categoryId]);

  const visible = useMemo(
    () => notes.filter((n) => matchesTriggers(n, clientPreferences)),
    [notes, clientPreferences],
  );

  const meta = STAGE_META[stage] || STAGE_META.pre;
  const Icon = meta.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh]" data-testid={`host-note-modal-${stage}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {meta.label}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[60vh] -mx-2 px-2">
          {loading ? (
            <div className="py-6 text-center text-sm text-gray-500">Loading…</div>
          ) : visible.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-500" data-testid="host-note-empty">
              No notes from your host for this stage.
            </div>
          ) : (
            <div className="space-y-3">
              {visible.map((n) => (
                <Card key={n.note_id} className={`border ${meta.accent}`} data-testid={`host-note-${n.note_id}`}>
                  <CardContent className="p-4 space-y-2">
                    <div className="font-medium text-sm">{n.title}</div>
                    {n.image_url && (
                      <img src={n.image_url} alt={n.title} className="w-full max-h-48 object-cover rounded-lg" />
                    )}
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
        <div className="flex justify-end pt-3 border-t">
          <Button onClick={onClose} variant="outline" size="sm" data-testid="host-note-close">
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HostNoteModal;
